import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Info } from 'lucide-react';
import {
  format, parseISO, startOfMonth, endOfMonth, subMonths, subDays,
  startOfYear, endOfYear,
} from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { aggregationHint } from '../lib/dateRangeUtils';

/**
 * Date range picker giornaliero — pattern unico per tutte le pagine
 * (Panoramica, Dashboard Area, Pagina Negozio) dopo il refactor del
 * 2026-05-08 (idea_e245f72a3ede, idea_d61fb5573eba). Mostra il range
 * selezionato sul trigger, apre un popover con preset rapidi + calendar
 * 2-mesi (italiano) + bottone "Applica".
 *
 * API:
 *  - `from`, `to`: stringhe ISO "yyyy-MM-dd"
 *  - `onChange(from, to)`: chiamato solo all'Applica o su preset (NON
 *    su ogni clic del calendar, per evitare refetch eccessivi)
 *  - `align`: "start" | "end" (default "end" — allineato al lato dx)
 *  - `testIdPrefix`: per i data-testid (default "daterange")
 *  - `extraPreset`: preset custom opzionale `{label, getRange: () => {from,to}}`
 */
export default function DateRangePicker({
  from, to, onChange, align = 'end', testIdPrefix = 'daterange', extraPreset,
}) {
  const [open, setOpen] = useState(false);
  const today = useMemo(() => new Date(), []);
  const [draft, setDraft] = useState({
    from: from ? parseISO(from) : null,
    to: to ? parseISO(to) : null,
  });

  // Sync il draft con la prop quando il consumer cambia from/to esternamente
  // (es. preset apply, navigation con URL state, "saved view"). Lo facciamo
  // anche su `open` change per ripartire pulito ad ogni riapertura.
  useEffect(() => {
    setDraft({
      from: from ? parseISO(from) : null,
      to: to ? parseISO(to) : null,
    });
  }, [from, to, open]);

  const fmtTrigger = (() => {
    if (!from || !to) return 'Seleziona periodo';
    const f = parseISO(from);
    const t = parseISO(to);
    const sameDay = format(f, 'yyyy-MM-dd') === format(t, 'yyyy-MM-dd');
    if (sameDay) return format(f, 'd MMM yyyy', { locale: itLocale });
    const fMonthStart = format(startOfMonth(f), 'yyyy-MM-dd');
    const fMonthEnd = format(endOfMonth(f), 'yyyy-MM-dd');
    if (
      fMonthStart === format(f, 'yyyy-MM-dd') &&
      fMonthEnd === format(t, 'yyyy-MM-dd') &&
      f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear()
    ) {
      return format(f, 'MMMM yyyy', { locale: itLocale });
    }
    // Anno intero?
    if (
      format(startOfYear(f), 'yyyy-MM-dd') === format(f, 'yyyy-MM-dd') &&
      format(endOfYear(f), 'yyyy-MM-dd') === format(t, 'yyyy-MM-dd')
    ) {
      return `Anno ${f.getFullYear()}`;
    }
    return `${format(f, 'd MMM yy', { locale: itLocale })} → ${format(t, 'd MMM yy', { locale: itLocale })}`;
  })();

  const applyPreset = (label) => {
    const refYear = today.getFullYear();
    let f, t;
    if (label === 'this-month') { f = startOfMonth(today); t = endOfMonth(today); }
    else if (label === 'last-month') { const ls = subMonths(today, 1); f = startOfMonth(ls); t = endOfMonth(ls); }
    else if (label === 'last-7') { f = subDays(today, 6); t = today; }
    else if (label === 'last-14') { f = subDays(today, 13); t = today; }
    else if (label === 'last-30') { f = subDays(today, 29); t = today; }
    else if (label === 'q1') { f = new Date(refYear, 0, 1); t = new Date(refYear, 2, 31); }
    else if (label === 'q2') { f = new Date(refYear, 3, 1); t = new Date(refYear, 5, 30); }
    else if (label === 'q3') { f = new Date(refYear, 6, 1); t = new Date(refYear, 8, 30); }
    else if (label === 'q4') { f = new Date(refYear, 9, 1); t = new Date(refYear, 11, 31); }
    else if (label === 'year') { f = startOfYear(today); t = endOfYear(today); }
    else if (label === 'extra' && extraPreset) {
      const r = extraPreset.getRange();
      onChange(r.from, r.to);
      setOpen(false);
      return;
    } else return;
    onChange(format(f, 'yyyy-MM-dd'), format(t, 'yyyy-MM-dd'));
    setOpen(false);
  };

  const apply = () => {
    if (draft.from && draft.to) {
      onChange(format(draft.from, 'yyyy-MM-dd'), format(draft.to, 'yyyy-MM-dd'));
    } else if (draft.from && !draft.to) {
      onChange(format(draft.from, 'yyyy-MM-dd'), format(draft.from, 'yyyy-MM-dd'));
    }
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="rounded-none h-9 px-3 justify-start font-normal min-w-[16rem]"
            data-testid={`${testIdPrefix}-trigger`}
          >
            <CalendarDays className="w-4 h-4 text-slate-500 mr-2" />
            <span className="text-sm truncate">{fmtTrigger}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 rounded-none" align={align}>
        <div className="flex">
          <div className="border-r border-slate-200 p-2 flex flex-col gap-1 min-w-[10rem]">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 px-2 pb-1">Preset</p>
            <PresetBtn onClick={() => applyPreset('last-7')} testId={`${testIdPrefix}-preset-last-7`}>Ultimi 7 giorni</PresetBtn>
            <PresetBtn onClick={() => applyPreset('last-14')} testId={`${testIdPrefix}-preset-last-14`}>Ultimi 14 giorni</PresetBtn>
            <PresetBtn onClick={() => applyPreset('last-30')} testId={`${testIdPrefix}-preset-last-30`}>Ultimi 30 giorni</PresetBtn>
            <div className="h-px bg-slate-100 my-1" />
            <PresetBtn onClick={() => applyPreset('this-month')} testId={`${testIdPrefix}-preset-this-month`}>Mese corrente</PresetBtn>
            <PresetBtn onClick={() => applyPreset('last-month')} testId={`${testIdPrefix}-preset-last-month`}>Mese scorso</PresetBtn>
            <PresetBtn onClick={() => applyPreset('q1')} testId={`${testIdPrefix}-preset-q1`}>Q1</PresetBtn>
            <PresetBtn onClick={() => applyPreset('q2')} testId={`${testIdPrefix}-preset-q2`}>Q2</PresetBtn>
            <PresetBtn onClick={() => applyPreset('q3')} testId={`${testIdPrefix}-preset-q3`}>Q3</PresetBtn>
            <PresetBtn onClick={() => applyPreset('q4')} testId={`${testIdPrefix}-preset-q4`}>Q4</PresetBtn>
            <PresetBtn onClick={() => applyPreset('year')} testId={`${testIdPrefix}-preset-year`}>Anno {today.getFullYear()}</PresetBtn>
            {extraPreset && (
              <PresetBtn onClick={() => applyPreset('extra')} testId={`${testIdPrefix}-preset-extra`}>{extraPreset.label}</PresetBtn>
            )}
          </div>
          <div className="flex flex-col">
            <Calendar
              mode="range"
              numberOfMonths={2}
              defaultMonth={draft.from || today}
              selected={{ from: draft.from || undefined, to: draft.to || undefined }}
              onSelect={(range) => setDraft({ from: range?.from || null, to: range?.to || null })}
              locale={itLocale}
            />
            <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 bg-slate-50">
              <p className="text-xs text-slate-600">
                {draft.from
                  ? `${format(draft.from, 'd MMM yy', { locale: itLocale })}${draft.to ? ` → ${format(draft.to, 'd MMM yy', { locale: itLocale })}` : ' →'}`
                  : 'Seleziona inizio e fine'}
              </p>
              <Button
                size="sm" variant="default"
                className="rounded-none h-8 bg-black text-white"
                onClick={apply}
                disabled={!draft.from}
                data-testid={`${testIdPrefix}-apply`}
              >
                Applica
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
    {/* Hint "Vista parziale" — appare solo quando il range non coincide con
        un mese/quarter/anno intero. Aiuta l'AM a capire che i KPI mensili
        (Health Score, Mesi in target, ecc.) sotto stanno comunque
        aggregando sull'intero mese di `dateFrom`. */}
    {(() => {
      const hint = aggregationHint(from, to);
      if (!hint) return null;
      return (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium bg-amber-50 border border-amber-300 text-amber-900 whitespace-nowrap"
                data-testid={`${testIdPrefix}-partial-hint`}
              >
                <Info className="w-3 h-3" />
                Vista parziale
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-xs">
              {hint}. I KPI a granularità mensile (Health Score, mesi in target) restano calcolati sull'intero mese.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    })()}
    </div>
  );
}

function PresetBtn({ children, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left text-xs px-2 py-1.5 hover:bg-slate-50 border border-transparent hover:border-slate-200 rounded-none"
      data-testid={testId}
    >
      {children}
    </button>
  );
}
